import {
  implantedItemCharacteristicsSchema,
  ItemTypeEnum,
  type ImplantedItemCharacteristics,
} from "@jasper/client-models";
import {
  ImplantedItemCharacteristicsForm,
  validateSchema,
} from "@jasper/forms";
import { Alert, Loader, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigurableProduct, ProductStepProps } from "./types";
import { useRegisterStepSave } from "./useRegisterStepSave";

const validateSelection = ({
  value,
}: {
  value: Partial<ImplantedItemCharacteristics> | undefined;
}) => {
  const result = validateSchema(implantedItemCharacteristicsSchema, value);

  if (!result.success) {
    return result.errors;
  }

  return undefined;
};

export const ImplantRetentionStep = ({
  products,
  orderConfiguration,
  stepKey,
  registerStepSave,
}: ProductStepProps) => {
  const { isLoading: isOrderLoading, updateProducts } = orderConfiguration;
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.implantRetention",
  });

  const [product] = products;

  const persistedItem = product?.items.find(
    item =>
      item.type === ItemTypeEnum.implantCrown ||
      item.type === ItemTypeEnum.bridgeImplant
  );

  const defaultValues: {
    implantedItemCharacteristics: Partial<ImplantedItemCharacteristics>;
  } = useMemo(() => {
    if (!persistedItem || !persistedItem.implantedItemCharacteristics) {
      return { implantedItemCharacteristics: {} };
    }

    return {
      implantedItemCharacteristics: persistedItem.implantedItemCharacteristics,
    };
  }, [persistedItem]);

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const buildItems = (configuredProduct: ConfigurableProduct) =>
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        configuredProduct.items.map(({ id: _id, ...item }) => {
          if (
            item.type !== ItemTypeEnum.implantCrown &&
            item.type !== ItemTypeEnum.bridgeImplant
          ) {
            return item;
          }

          return {
            ...item,
            implantedItemCharacteristics:
              value.implantedItemCharacteristics as ImplantedItemCharacteristics,
          };
        });

      try {
        await updateProducts(
          products.map(configuredProduct => ({
            productId: configuredProduct.id,
            items: buildItems(configuredProduct),
          }))
        );
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset(defaultValues);
    }
  }, [product, defaultValues, form]);

  if (isOrderLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">
          {t("productNotFound", { keyPrefix: "common" })}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          {t("title")}
        </Title>
        <form.Field
          name="implantedItemCharacteristics"
          validators={{
            onChange: ({ value, fieldApi }) => {
              const result = validateSelection({ value });

              if (!result) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return result;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            return (
              <ImplantedItemCharacteristicsForm
                value={field.state.value}
                errors={formErrors}
                onChange={next => field.setValue(next)}
                onBlur={field.handleBlur}
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
